import { useState, useEffect } from 'react';
import { Users, Camera, Video, Edit, CheckCircle, Instagram, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import BookingModal from '../components/BookingModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Professionals = () => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingTarget, setBookingTarget] = useState(null);

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    try {
      const response = await axios.get(`${API}/professionals`);
      setProfessionals(response.data);
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setLoading(false);
    }
  };

  const photographers = professionals.filter(pro => pro.role === "Photographer");
  const videographers = professionals.filter(pro => pro.role === "Videographer");
  const editors = professionals.filter(pro => pro.role === "Photo Editor" || pro.role === "Video Editor");

  const getRoleIcon = (role) => {
    if (role === "Photographer") return Camera;
    if (role === "Videographer") return Video;
    return Edit;
  };

  const ProfessionalCard = ({ professional }) => {
    const IconComponent = getRoleIcon(professional.role);
    
    // Check if image_url is an external URL or a local path
    const getImageUrl = (imageUrl) => {
      if (!imageUrl) return null;
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      return `${BACKEND_URL}${imageUrl}`;
    };
    
    return (
      <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 hover:border-red-600/50 transition-all" data-testid={`professional-${professional.id}`}>
        <div className="flex items-start gap-4 mb-4">
          {/* Profile Image or Icon */}
          {professional.image_url ? (
            <div className="flex-shrink-0 w-16 h-16 rounded-full overflow-hidden border-2 border-red-600/30">
              <img 
                src={getImageUrl(professional.image_url)}
                alt={professional.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="flex-shrink-0 w-16 h-16 rounded-full bg-red-600/10 flex items-center justify-center">
              <IconComponent className="text-red-500" size={28} />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-white mb-1">{professional.name}</h3>
            <p className="text-red-500 font-mono text-sm tracking-wider uppercase">{professional.role}</p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-300 leading-relaxed mb-4">{professional.description}</p>
          
          {professional.specialties && professional.specialties.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-white mb-2">Specialties:</h4>
              <div className="flex flex-wrap gap-2">
                {professional.specialties.map((specialty, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 rounded-full bg-red-600/10 border border-red-600/30 text-red-400 text-xs font-mono tracking-wider uppercase"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-500 text-xs mb-1">Experience</p>
              <p className="text-white text-sm font-semibold">{professional.experience || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Availability</p>
              <p className={`text-sm font-semibold ${
                professional.availability === "Available for bookings" 
                  ? "text-green-400" 
                  : "text-yellow-400"
              }`}>
                {professional.availability || 'Contact for availability'}
              </p>
            </div>
          </div>

          {/* Instagram Link */}
          {professional.instagram_url && (
            <div className="mb-4">
              <a 
                href={professional.instagram_url}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 text-sm transition-colors"
              >
                <Instagram size={16} />
                {professional.instagram_url.replace('https://www.instagram.com/', '@').replace('/', '')}
              </a>
            </div>
          )}

          {professional.portfolio && !professional.instagram_url && (
            <div>
              <p className="text-gray-500 text-xs mb-1">Portfolio</p>
              <a 
                href={professional.portfolio.startsWith('http') ? professional.portfolio : `https://${professional.portfolio}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-red-500 hover:text-red-400 text-sm transition-colors"
              >
                {professional.portfolio}
              </a>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-red-900/20">
            <button
              onClick={() => setBookingTarget(professional)}
              className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full font-mono text-xs tracking-widest uppercase transition-colors"
              data-testid={`book-pro-${professional.id}`}
            >
              <Calendar size={14} /> Request Booking
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20 flex justify-center items-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-600/10 flex items-center justify-center">
              <Users className="text-red-500" size={36} />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-red-500 mb-6 uppercase">
            Professional Directory
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-8">
            Talented freelance photographers, videographers, and editors who have worked with EWS Productions on multiple shoots
          </p>
          <Link 
            to="/professional-signup"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
            data-testid="join-team-btn"
          >
            Join Our Team
          </Link>
        </div>

        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
            <h2 className="text-2xl font-semibold text-white mb-4">In-House Professional Network</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              At EWS Productions, we've built strong relationships with exceptional creative professionals who understand our studio environments, maintain the highest standards of discretion, and consistently deliver outstanding results.
            </p>
            <p className="text-gray-300 leading-relaxed">
              All professionals listed have been vetted, have extensive experience working in our spaces, and share our commitment to professionalism, consent, and artistic excellence. While you're welcome to bring your own team, our in-house professionals offer seamless integration with our facilities and workflows.
            </p>
          </div>
        </div>

        {/* Photographers Section */}
        {photographers.length > 0 && (
          <div className="max-w-6xl mx-auto mb-20">
            <div className="flex items-center gap-3 mb-8">
              <Camera className="text-red-500" size={32} />
              <h2 className="text-3xl font-semibold text-white">Photographers</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {photographers.map((professional) => (
                <ProfessionalCard key={professional.id} professional={professional} />
              ))}
            </div>
          </div>
        )}

        {/* Videographers Section */}
        {videographers.length > 0 && (
          <div className="max-w-6xl mx-auto mb-20">
            <div className="flex items-center gap-3 mb-8">
              <Video className="text-red-500" size={32} />
              <h2 className="text-3xl font-semibold text-white">Videographers</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videographers.map((professional) => (
                <ProfessionalCard key={professional.id} professional={professional} />
              ))}
            </div>
          </div>
        )}

        {/* Editors Section */}
        {editors.length > 0 && (
          <div className="max-w-6xl mx-auto mb-20">
            <div className="flex items-center gap-3 mb-8">
              <Edit className="text-red-500" size={32} />
              <h2 className="text-3xl font-semibold text-white">Editors & Post-Production</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {editors.map((professional) => (
                <ProfessionalCard key={professional.id} professional={professional} />
              ))}
            </div>
          </div>
        )}

        {/* Benefits Section */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Benefits of Using Our In-House Professionals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="flex justify-center mb-4">
                <CheckCircle className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 text-center">Studio Familiarity</h3>
              <p className="text-gray-400 text-center">
                Already familiar with our equipment, lighting setups, and space layouts for efficient shoots
              </p>
            </div>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="flex justify-center mb-4">
                <CheckCircle className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 text-center">Vetted Professionals</h3>
              <p className="text-gray-400 text-center">
                All professionals thoroughly vetted for skills, reliability, and adherence to consent protocols
              </p>
            </div>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="flex justify-center mb-4">
                <CheckCircle className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 text-center">Seamless Coordination</h3>
              <p className="text-gray-400 text-center">
                Easy booking coordination through EWS Productions for streamlined project management
              </p>
            </div>
          </div>
        </div>

        {/* Join CTA Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Join Our Directory</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/model-signup"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              data-testid="join-model-cta"
            >
              Join as Model →
            </Link>
            <Link 
              to="/join-our-team"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-red-600 hover:bg-red-600/10 text-red-500 px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              data-testid="join-photographer-cta"
            >
              Join as Photographer
            </Link>
          </div>
        </div>

        {/* Booking CTA */}
        <div className="max-w-4xl mx-auto text-center bg-red-900/10 rounded-lg p-8 lg:p-12 border border-red-600/30">
          <h2 className="text-3xl font-semibold text-white mb-4">Interested in Working with Our Professionals?</h2>
          <p className="text-gray-300 mb-8 leading-relaxed">
            When booking your studio session, let us know if you'd like to work with any of our in-house professionals. We'll coordinate availability and provide rates.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
            data-testid="contact-cta"
          >
            Contact Us to Book
          </Link>
        </div>

        {/* Note */}
        <div className="max-w-4xl mx-auto mt-12 text-center">
          <p className="text-gray-500 text-sm italic">
            All professionals operate as independent freelancers. Rates and availability vary. Contact us for current pricing and booking.
          </p>
        </div>
      </div>

      <BookingModal
        open={!!bookingTarget}
        onClose={() => setBookingTarget(null)}
        talentType="professional"
        talentId={bookingTarget?.id}
        talentName={bookingTarget?.name || ''}
      />
    </div>
  );
};

export default Professionals;
